// Detailed Content Audit exports: CSV + branded printable HTML report.
// Styling mirrors the SERPfinity dashboard (blue-600 -> blue-800 header,
// score ring, severity colours) so downloads look like the website.

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2, pass: 3 };

const SEVERITY_STYLE = {
  critical: { label: 'Critical', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  warning: { label: 'Warning', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  info: { label: 'Info', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  pass: { label: 'Passed', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
};

const CATEGORY_LABELS = {
  seo: 'SEO',
  content: 'Content Quality',
  technical: 'Technical',
  media: 'Media',
  links: 'Links',
  performance: 'Performance',
};

const CATEGORY_ORDER = ['seo', 'content', 'technical', 'media', 'links', 'performance'];

function scoreColor(score) {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
}

function esc(value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function csvCell(value) {
  const s = String(value === null || value === undefined ? '' : value);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

const GROUP_LABELS = {
  items: 'Item',
  internalItems: 'Internal link',
  externalItems: 'External link',
  types: 'Type',
};

function groupLabel(key) {
  if (GROUP_LABELS[key]) return GROUP_LABELS[key];
  return String(key).replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

// Split an issue's `details` object into a short summary line plus the list of
// affected items (links, headings, image sources) so nothing is lost.
function readDetails(details) {
  const summary = [];
  const items = [];
  if (!details || typeof details !== 'object') return { summary: '', items };
  Object.keys(details).forEach((key) => {
    const value = details[key];
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry && typeof entry === 'object') {
          items.push({
            group: key,
            value: entry.href || entry.src || entry.tag || '',
            text: entry.text || entry.alt || '',
            extra: entry.isInternal === undefined ? '' : (entry.isInternal ? 'internal' : 'external'),
          });
        } else {
          items.push({ group: key, value: String(entry), text: '', extra: '' });
        }
      });
    } else if (value && typeof value === 'object') {
      Object.keys(value).forEach((k) => summary.push(groupLabel(k) + ': ' + value[k]));
    } else if (value !== '' && value !== null && value !== undefined) {
      summary.push(groupLabel(key) + ': ' + value);
    }
  });
  return { summary: summary.join(' | '), items };
}

function categoryKeys(data) {
  const cats = (data && data.categories) || {};
  const known = CATEGORY_ORDER.filter((k) => cats[k]);
  const extra = Object.keys(cats).filter((k) => !CATEGORY_ORDER.includes(k));
  return known.concat(extra);
}

function issueRows(data, catKey) {
  const cat = ((data && data.categories) || {})[catKey] || {};
  const rows = (cat.issues || []).map((issue) => Object.assign({
    categoryKey: catKey,
    category: CATEGORY_LABELS[catKey] || catKey,
    categoryScore: cat.score,
    id: issue.id,
    title: issue.title || '',
    severity: issue.severity || 'info',
    description: issue.description || '',
    recommendation: issue.recommendation || '',
  }, readDetails(issue.details)));
  rows.sort((a, b) => {
    const sa = SEVERITY_ORDER[a.severity];
    const sb = SEVERITY_ORDER[b.severity];
    return (sa === undefined ? 9 : sa) - (sb === undefined ? 9 : sb);
  });
  return rows;
}

function fileStamp(data) {
  let host = 'page';
  try {
    host = new URL(data.url).hostname.replace(/^www\./, '');
  } catch (e) {
    host = 'page';
  }
  const when = new Date(data.auditDate || Date.now());
  return host + '-content-audit-' + when.toISOString().slice(0, 10);
}

export function buildContentAuditCsv(data) {
  const meta = data.meta || {};
  const summary = data.summary || {};
  const cats = data.categories || {};
  const lines = [];
  const row = (cells) => lines.push(cells.map(csvCell).join(','));

  row(['SERPfinity Content Audit Report']);
  row(['Audited URL', data.url || '']);
  row(['Audit date', new Date(data.auditDate || Date.now()).toLocaleString()]);
  row(['Overall score', (data.score === undefined ? '' : data.score) + ' / 100']);
  row([]);
  row(['Critical', summary.critical || 0]);
  row(['Warnings', summary.warnings || 0]);
  row(['Passed', summary.passed || 0]);
  row(['Info', summary.info || 0]);
  row([]);
  row(['Category scores']);
  row(['Category', 'Score']);
  categoryKeys(data).forEach((k) => row([CATEGORY_LABELS[k] || k, cats[k].score]));
  row([]);
  row(['Page facts']);
  row(['Title', meta.title || '']);
  row(['Meta description', meta.metaDescription || '']);
  row(['Canonical URL', meta.canonical || '']);
  row(['Word count', meta.wordCount]);
  row(['Images', meta.imageCount]);
  row(['Links', meta.linkCount]);
  row(['HTML size (KB)', meta.htmlSizeKB]);
  row([]);
  row(['Issues and affected items']);
  row(['Category', 'Severity', 'Issue', 'Description', 'Recommendation', 'Details', 'Affected items', 'Item', 'Anchor / text', 'Scope']);
  categoryKeys(data).forEach((k) => {
    issueRows(data, k).forEach((issue) => {
      const badge = (SEVERITY_STYLE[issue.severity] || {}).label || issue.severity;
      row([issue.category, badge, issue.title, issue.description, issue.recommendation,
        issue.summary, issue.items.length, '', '', '']);
      issue.items.forEach((item) => {
        row(['', '', issue.title, '', '', groupLabel(item.group), '', item.value, item.text, item.extra]);
      });
    });
  });
  return lines.join('\r\n');
}

export function downloadContentAuditCsv(data) {
  if (!data) return false;
  const blob = new Blob(['\ufeff' + buildContentAuditCsv(data)], { type: 'text/csv;charset=utf-8;' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileStamp(data) + '.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return true;
}

const REPORT_CSS = `
*{box-sizing:border-box}
body{margin:0;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#111827;background:#f9fafb;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.hero{background:linear-gradient(90deg,#2563eb,#1e40af);color:#fff;padding:30px 40px 34px}
.brand{display:flex;align-items:center;gap:10px;font-size:19px;font-weight:800}
.brand .pro{font-size:10px;font-weight:700;letter-spacing:.06em;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35);border-radius:9999px;padding:2px 8px}
.crumbs{margin-top:20px;font-size:12px;color:#bfdbfe}
.hero h1{margin:6px 0 4px;font-size:34px;font-weight:800;letter-spacing:-.02em}
.hero .sub{margin:0;font-size:14px;color:#dbeafe}
.hero .target{margin-top:18px;font-size:13px;color:#e0e7ff;word-break:break-all}
.card{background:#fff;border:1px solid #e5e7eb;border-radius:14px;box-shadow:0 1px 3px rgba(0,0,0,.06);padding:24px;margin:24px 40px}
.score{display:flex;align-items:center;gap:28px;flex-wrap:wrap}
.ringwrap{text-align:center}
.ringwrap .cap{font-size:12px;color:#4b5563;margin-top:4px}
.tiles{display:flex;gap:14px;flex:1;flex-wrap:wrap}
.tile{flex:1;min-width:110px;border-radius:12px;padding:14px 10px;text-align:center;border:1px solid}
.tile b{display:block;font-size:26px;line-height:1.15}
.tile span{font-size:12px}
.bars{display:grid;grid-template-columns:repeat(2,1fr);gap:12px 26px;margin-top:26px}
.bar{display:flex;align-items:center;gap:10px;font-size:13px}
.bar .label{width:104px;color:#374151}
.bar .track{flex:1 1 auto;min-width:96px;height:8px;background:#e5e7eb;border-radius:9999px;overflow:hidden}
.bar .fill{display:block;height:8px;border-radius:9999px}
.bar .val{width:30px;text-align:right;font-weight:700}
h2.sec{margin:34px 40px 0;font-size:18px;font-weight:700}
h2.sec small{font-weight:500;color:#6b7280;font-size:13px;margin-left:8px}
table.facts{width:100%;border-collapse:collapse;font-size:13px}
table.facts td{padding:9px 0;border-bottom:1px solid #f3f4f6;vertical-align:top;word-break:break-all}
table.facts td:first-child{width:190px;color:#6b7280;word-break:normal}
.issue{border:1px solid;border-radius:12px;padding:14px 16px;margin-bottom:12px;page-break-inside:avoid}
.issue .top{display:flex;justify-content:space-between;gap:14px;align-items:baseline}
.issue h3{margin:0;font-size:14px;font-weight:700}
.issue .badge{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap}
.issue .desc{margin:8px 0 0;font-size:13px;color:#374151}
.issue .rec{margin:9px 0 0;font-size:13px;padding:7px 11px;border-radius:6px;background:rgba(255,255,255,.75);border-left:3px solid}
.issue .facts2{margin-top:7px;font-size:12px;color:#6b7280}
table.items{width:100%;border-collapse:collapse;margin-top:11px;font-size:12px;background:#fff}
table.items th{background:#f9fafb;color:#6b7280;text-align:left;font-weight:600;padding:6px 8px;border:1px solid #e5e7eb}
table.items td{padding:6px 8px;border:1px solid #f3f4f6;word-break:break-all;vertical-align:top}
.foot{margin:36px 40px 0;font-size:11px;color:#9ca3af;text-align:center}
.noprint{text-align:center;margin:22px 40px 0}
.noprint button{background:#2563eb;color:#fff;border:none;border-radius:9px;padding:11px 20px;font-size:14px;font-weight:600;cursor:pointer}
@media print{.noprint{display:none}body{background:#fff}.card{box-shadow:none}.tiles{display:grid;grid-template-columns:1fr 1fr;gap:12px}.bars{grid-template-columns:repeat(2,1fr)}@page{size:A4;margin:11mm}}
`;

const LOGO_SVG = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3"/></svg>';

function ringSvg(score) {
  const total = 402;
  const value = Math.max(0, Math.min(100, Number(score) || 0));
  const dash = (value / 100) * total;
  const color = scoreColor(value);
  return `<svg width="146" height="146" viewBox="0 0 146 146">`
    + `<circle cx="73" cy="73" r="64" fill="none" stroke="#e5e7eb" stroke-width="10"></circle>`
    + `<circle cx="73" cy="73" r="64" fill="none" stroke="${color}" stroke-width="10" stroke-linecap="round" stroke-dasharray="${dash} ${total}" transform="rotate(-90 73 73)"></circle>`
    + `<text x="73" y="72" text-anchor="middle" font-size="36" font-weight="800" fill="${color}">${value}</text>`
    + `<text x="73" y="94" text-anchor="middle" font-size="13" fill="#6b7280">/ 100</text></svg>`;
}

function itemsTable(items) {
  if (!items || !items.length) return '';
  const scope = items.some((i) => i.extra);
  const head = '<tr><th style="width:22%">Detail</th><th>Item</th><th style="width:26%">Anchor / text</th>'
    + (scope ? '<th style="width:70px">Scope</th>' : '') + '</tr>';
  const body = items.map((i) => '<tr><td>' + esc(groupLabel(i.group)) + '</td><td>' + esc(i.value)
    + '</td><td>' + esc(i.text) + '</td>' + (scope ? '<td>' + esc(i.extra) + '</td>' : '') + '</tr>').join('');
  return '<table class="items">' + head + body + '</table>';
}

export function buildContentAuditReportHtml(data) {
  const meta = data.meta || {};
  const summary = data.summary || {};
  const cats = data.categories || {};
  const when = new Date(data.auditDate || Date.now());
  const keys = categoryKeys(data);

  const tiles = [
    ['Critical', summary.critical || 0, SEVERITY_STYLE.critical],
    ['Warnings', summary.warnings || 0, SEVERITY_STYLE.warning],
    ['Passed', summary.passed || 0, SEVERITY_STYLE.pass],
    ['Info', summary.info || 0, SEVERITY_STYLE.info],
  ].map((t) => `<div class="tile" style="background:${t[2].bg};border-color:${t[2].border};color:${t[2].color}"><b>${t[1]}</b><span>${t[0]}</span></div>`).join('');

  const bars = keys.map((k) => {
    const s = Number(cats[k].score) || 0;
    return `<div class="bar"><span class="label">${esc(CATEGORY_LABELS[k] || k)}</span>`
      + `<span class="track"><span class="fill" style="width:${s}%;background:${scoreColor(s)}"></span></span>`
      + `<span class="val" style="color:${scoreColor(s)}">${s}</span></div>`;
  }).join('');

  const facts = [
    ['Audited URL', data.url],
    ['Page title', meta.title],
    ['Meta description', meta.metaDescription],
    ['Canonical URL', meta.canonical],
    ['Word count', meta.wordCount],
    ['Images', meta.imageCount],
    ['Links', meta.linkCount],
    ['HTML size', meta.htmlSizeKB === undefined ? '' : meta.htmlSizeKB + ' KB'],
  ].map((f) => `<tr><td>${esc(f[0])}</td><td>${f[1] === undefined || f[1] === null || f[1] === '' ? '&mdash;' : esc(f[1])}</td></tr>`).join('');

  const sections = keys.map((k) => {
    const rows = issueRows(data, k);
    const s = Number(cats[k].score) || 0;
    const body = rows.map((issue) => {
      const st = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.info;
      return `<div class="issue" style="background:${st.bg};border-color:${st.border}">`
        + `<div class="top"><h3 style="color:${st.color}">${esc(issue.title)}</h3>`
        + `<span class="badge" style="color:${st.color}">${st.label}</span></div>`
        + (issue.description ? `<p class="desc">${esc(issue.description)}</p>` : '')
        + (issue.summary ? `<div class="facts2">${esc(issue.summary)}</div>` : '')
        + (issue.recommendation ? `<p class="rec" style="border-left-color:${st.color}"><strong>Recommendation:</strong> ${esc(issue.recommendation)}</p>` : '')
        + itemsTable(issue.items) + `</div>`;
    }).join('');
    return `<h2 class="sec">${esc(CATEGORY_LABELS[k] || k)}<small>${rows.length} checks &middot; score ${s}/100</small></h2>`
      + `<div class="card">${body || '<p class="desc">No checks reported.</p>'}</div>`;
  }).join('');

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">`
    + `<title>Content Audit &mdash; ${esc(data.url || '')}</title><style>${REPORT_CSS}</style></head><body>`
    + `<div class="hero"><div class="brand">${LOGO_SVG}<span>SERPfinity&trade;</span><span class="pro">PRO</span></div>`
    + `<div class="crumbs">Dashboard / Content Marketing / Content Audit</div>`
    + `<h1>Content Audit</h1><p class="sub">Analyze any page for SEO, content quality, and technical health</p>`
    + `<div class="target">${esc(data.url || '')} &nbsp;&middot;&nbsp; ${esc(when.toLocaleString())}</div></div>`
    + `<div class="noprint"><button onclick="window.print()">Save as PDF / Print</button></div>`
    + `<div class="card"><div class="score"><div class="ringwrap">${ringSvg(data.score)}<div class="cap">Overall Score</div></div>`
    + `<div class="tiles">${tiles}</div></div><div class="bars">${bars}</div></div>`
    + `<h2 class="sec">Page facts</h2><div class="card"><table class="facts">${facts}</table></div>`
    + sections
    + `<div class="foot">Generated by SERPfinity&trade; Content Audit &middot; ${esc(when.toLocaleString())}</div></body></html>`;
}

export function openContentAuditReport(data) {
  if (!data) return false;
  const blob = new Blob([buildContentAuditReportHtml(data)], { type: 'text/html;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const win = window.open(href, '_blank');
  if (!win) {
    URL.revokeObjectURL(href);
    return false;
  }
  win.focus();
  setTimeout(() => URL.revokeObjectURL(href), 60000);
  return true;
}

// Server-side PDF (Puppeteer) download. Throws on failure so callers can fall back.
export async function downloadContentAuditPdf(data) {
  if (!data) return false;
  const html = buildContentAuditReportHtml(data);
  const filename = fileStamp(data) + '.pdf';
  const token = localStorage.getItem('token');
  const res = await fetch('/report-pdf.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token, filename: filename, html: html })
  });
  if (!res.ok) {
    let msg = 'PDF service error ' + res.status;
    try { const j = await res.json(); if (j && j.message) msg = j.message; } catch (e) { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(href), 4000);
  return filename;
}
